import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, Edit, Package, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { slug: "", name: "", price: 49, currency: "USD", days: 30, max_clients: 5, description: "", is_active: true };

export default function AdminResellerPlans() {
  const { getAdminAxios } = useOutletContext();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminAxios().get(`${API}/admin/reseller-plans`);
      setList(data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [getAdminAxios]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    setSaving(true); setError("");
    try {
      if (editing) {
        await getAdminAxios().put(`${API}/admin/reseller-plans/${editing.id}`, form);
      } else {
        await getAdminAxios().post(`${API}/admin/reseller-plans`, form);
      }
      setOpen(false); setEditing(null); setForm(emptyForm);
      await refresh();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Borrar el plan "${p.name}"?`)) return;
    try { await getAdminAxios().delete(`${API}/admin/reseller-plans/${p.id}`); await refresh(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="admin-reseller-plans-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><Package className="w-6 h-6" /> Planes de Reseller</h1>
          <p className="text-sm text-slate-400 mt-1">Planes que cada sub super admin contrata para operar. Definí precio, duración y límite de clientes.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="create-reseller-plan-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo plan
        </Button>
      </div>

      {error && <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">⚠️ {error}</div>}

      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-white text-base">Planes activos ({list.length})</CardTitle>
          <CardDescription className="text-slate-400">Solo los planes activos aparecen para los sub admins al pagar.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : list.length === 0 ? (
            <p className="text-sm text-slate-500">Sin planes todavía. Creá el primero.</p>
          ) : (
            <div className="space-y-2">
              {list.map((p) => (
                <div key={p.id} className="border border-white/10 rounded-lg p-4 flex items-center justify-between gap-4 bg-black/20" data-testid={`reseller-plan-row-${p.slug}`}>
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      {p.name}
                      {!p.is_active && <span className="text-[10px] uppercase tracking-wider bg-slate-500/20 text-slate-300 px-1.5 py-0.5 rounded">Inactivo</span>}
                    </p>
                    <p className="text-xs text-slate-400">slug: <code className="bg-black/30 px-1 rounded">{p.slug}</code> · {p.description || "—"}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-amber-400 font-bold">${p.price} {p.currency}</p>
                      <p className="text-xs text-slate-400">{p.days} días · {p.max_clients} clientes</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(p); setForm({ ...p }); setOpen(true); }} className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid={`edit-reseller-plan-${p.slug}`}><Edit className="w-3 h-3" /></Button>
                    <Button variant="outline" size="sm" onClick={() => remove(p)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-reseller-plan-${p.slug}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white max-w-xl" data-testid="reseller-plan-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Slug (id único)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!!editing} className="bg-white/5 border-white/10 text-white" data-testid="rp-slug" /></div>
              <div><Label className="text-slate-300">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="rp-name" /></div>
              <div><Label className="text-slate-300 flex items-center gap-1"><DollarSign className="w-3 h-3" />Precio</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || 0) })} className="bg-white/5 border-white/10 text-white" data-testid="rp-price" /></div>
              <div><Label className="text-slate-300">Moneda</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="rp-currency" /></div>
              <div><Label className="text-slate-300">Duración (días)</Label><Input type="number" value={form.days} onChange={(e) => setForm({ ...form, days: parseInt(e.target.value || 30) })} className="bg-white/5 border-white/10 text-white" data-testid="rp-days" /></div>
              <div><Label className="text-slate-300">Máx. clientes</Label><Input type="number" value={form.max_clients} onChange={(e) => setForm({ ...form, max_clients: parseInt(e.target.value || 5) })} className="bg-white/5 border-white/10 text-white" data-testid="rp-max-clients" /></div>
            </div>
            <div><Label className="text-slate-300">Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="rp-description" /></div>
            <div className="flex items-center justify-between bg-white/5 rounded px-3 py-2"><Label className="text-slate-200 text-sm">Activo</Label><Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="rp-active" /></div>
            {error && <p className="text-xs text-red-300">⚠️ {error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="rp-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {editing ? "Guardar" : "Crear plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
